import { useEffect, useState, useCallback } from 'react';
import type { TelegramWebApp, TelegramUser } from '../types';

interface UseTelegramReturn {
  webApp: TelegramWebApp | null;
  user: TelegramUser | null;
  initData: string;
  isReady: boolean;
  colorScheme: 'light' | 'dark';
  startParam: string | null;
  hapticFeedback: TelegramWebApp['HapticFeedback'] | null;
  showBackButton: () => void;
  hideBackButton: () => void;
  onBackButtonClick: (callback: () => void) => () => void;
  close: () => void;
  sendData: (data: object) => void;
  openLink: (url: string) => void;
  showAlert: (message: string) => Promise<void>;
  showConfirm: (message: string) => Promise<boolean>;
}

export function useTelegram(): UseTelegramReturn {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    
    if (tg) {
      setWebApp(tg);
      tg.ready();
      tg.expand();
      setIsReady(true);

      // Debug: Log Telegram WebApp info
      console.log('Telegram WebApp initialized:', {
        version: tg.version,
        platform: tg.platform,
        initData: tg.initData ? `${tg.initData.substring(0, 50)}...` : 'EMPTY',
        initDataLength: tg.initData?.length || 0,
        hasUser: !!tg.initDataUnsafe?.user,
      });

      // Set theme colors
      if (tg.colorScheme === 'dark') {
        document.body.classList.add('tg-dark');
      }
    } else {
      // Development mode - no Telegram context
      console.warn('Telegram WebApp not available - running in development mode');
      setIsReady(true);
    }
  }, []);

  const showBackButton = useCallback(() => {
    webApp?.BackButton.show();
  }, [webApp]);

  const hideBackButton = useCallback(() => {
    webApp?.BackButton.hide();
  }, [webApp]);

  const onBackButtonClick = useCallback((callback: () => void): () => void => {
    webApp?.BackButton.onClick(callback);
    return () => {
      webApp?.BackButton.offClick(callback);
    };
  }, [webApp]);

  const close = useCallback(() => {
    webApp?.close();
  }, [webApp]);

  const sendData = useCallback((data: object) => {
    webApp?.sendData(JSON.stringify(data));
  }, [webApp]);

  const openLink = useCallback((url: string) => {
    if (webApp) {
      webApp.openLink(url, { try_instant_view: true });
    } else {
      window.open(url, '_blank');
    }
  }, [webApp]);

  const showAlert = useCallback((message: string): Promise<void> => {
    return new Promise((resolve) => {
      if (webApp) {
        webApp.showAlert(message, resolve);
      } else {
        alert(message);
        resolve();
      }
    });
  }, [webApp]);

  const showConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (webApp) {
        webApp.showConfirm(message, resolve);
      } else {
        resolve(confirm(message));
      }
    });
  }, [webApp]);

  return {
    webApp,
    user: webApp?.initDataUnsafe.user ?? null,
    initData: webApp?.initData ?? '',
    isReady,
    colorScheme: webApp?.colorScheme ?? 'light',
    startParam: webApp?.initDataUnsafe.start_param ?? null,
    hapticFeedback: webApp?.HapticFeedback ?? null,
    showBackButton,
    hideBackButton,
    onBackButtonClick,
    close,
    sendData,
    openLink,
    showAlert,
    showConfirm,
  };
}

